import React, { useMemo, useState } from "react";
import authImage from "../assests/auth_image.png";
import { register as apiRegister, login as apiLogin, uploadToQdrant as apiupload} from "../api/schemeApi";
import "./Authentication.css";

const Authentication = ({ onBack, onSuccess }) => {
    const [mode, setMode] = useState("register");
    const [showPassword, setShowPassword] = useState(false);
    const [authMessage, setAuthMessage] = useState("");
    const [authMessageType, setAuthMessageType] = useState("error");
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        password: "",
        confirmPassword: "",
        remember: false,
        agreeTerms: false,
    });

    // using API helpers from ../api/schemeApi

    const isRegister = mode === "register";
    const heading = isRegister ? "Create your account" : "Login to your account";
    const subheading = isRegister
        ? "Join administrative network."
        : "Welcome back. Enter your credentials to manage your fleet.";

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!formData.email.trim()) {
            setAuthMessage("Please enter your email address.");
            setAuthMessageType("error");
            return;
        }
        if (!formData.password.trim()) {
            setAuthMessage("Please enter your password.");
            setAuthMessageType("error");
            return;
        }

        const email = formData.email.trim().toLowerCase();

        try {
            if (isRegister) {
                if (!formData.fullName.trim()) {
                    setAuthMessage("Please enter your full name.");
                    setAuthMessageType("error");
                    return;
                }
                if (!formData.confirmPassword.trim()) {
                    setAuthMessage("Please confirm your password.");
                    setAuthMessageType("error");
                    return;
                }
                if (formData.password !== formData.confirmPassword) {
                    setAuthMessage("Passwords do not match.");
                    setAuthMessageType("error");
                    return;
                }

                try {
                    await apiRegister({ fullName: formData.fullName, email, password: formData.password });
                    setFormData((prev) => ({ ...prev, password: "", confirmPassword: "" }));
                    setAuthMessage("Registration successful. Please log in.");
                    setAuthMessageType("success");
                    setMode("login");
                    return;
                } catch (err) {
                    const msg = (err && (err.detail || err.message || err.error)) || String(err);
                    setAuthMessage(msg || "Registration failed");
                    setAuthMessageType("error");
                    return;
                }
            }
            // Login
            try {
                await apiLogin({ email, password: formData.password });
                setAuthMessage("");
                setAuthMessageType("success");
                if (onSuccess) onSuccess();
                else console.log("Authentication success", mode, formData);
            } catch (err) {
                const msg = (err && (err.detail || err.message || err.error)) || String(err);
                setAuthMessage(msg || "Invalid email or password.");
                setAuthMessageType("error");
                return;
            }
        } catch (e) {
            setAuthMessage(e.message || "Authentication error");
            setAuthMessageType("error");
        }
    };

    const topNav = useMemo(
        () => [
            { key: "login", label: "Login" },
            { key: "register", label: "Register" },
        ],
        []
    );

    const styles = null;

    React.useEffect(() => {
        if (typeof window !== "undefined" && window.history) {
            window.history.pushState({}, "", "/authenticate");
        }
    }, []);

    return (
        <div className="auth-page">
            <div className="auth-container">
                <section className="auth-side-panel">
                    <img
                        className="auth-side-background-image"
                        src={authImage}
                        alt="Authentication background"
                    />
                    <div className="auth-particle-layer" />
                    <div className="auth-data-overlay" />
                    <div className="auth-side-overlay" />
                    <div className="auth-side-content">
                        <div className="auth-side-brand">
                            <span className="auth-brand-icon">🌾</span>
                            <div>
                                <h1 className="auth-side-title">AgriTech Admin</h1>
                                <p className="auth-side-top-text">
                                    Harnessing precision data for a sustainable agricultural future. Manage your global fleet and monitor crop health in real time.
                                </p>
                            </div>
                        </div>

                        <div className="auth-side-highlight">
                            <h2 className="auth-side-headline">Optimizing Growth Through Data</h2>
                            <p className="auth-side-copy">
                                Access real-time telemetry and advanced soil analytics for the Greenfield ecosystem.
                            </p>
                            <div className="auth-side-highlights">
                                <div className="auth-side-highlight-card">
                                    <span className="auth-side-highlight-label">Active Fleet</span>
                                    <strong>42 Units</strong>
                                </div>
                                <div className="auth-side-highlight-card">
                                    <span className="auth-side-highlight-label">Soil Health</span>
                                    <strong>98% Prime</strong>
                                </div>
                            </div>
                        </div>

                        <div className="auth-side-footer">
                            © KSH
                        </div>
                    </div>
                </section>

                <main className="auth-right-panel">
                    <div className="auth-card">
                        <div className="auth-action-row">
                            <button
                                type="button"
                                className="auth-back-button"
                                onClick={() => {
                                    if (onBack) onBack();
                                    else window.history.back();
                                }}
                            >
                                ← Back to home
                            </button>
                        </div>
                        <div className="auth-nav-bar">
                            {topNav.map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    onClick={() => setMode(item.key)}
                                    className={`auth-nav-button ${mode === item.key ? "active" : ""}`}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>

                        <div className="auth-form-header">
                            <h2 className="auth-title">{heading}</h2>
                            <p className="auth-paragraph">{subheading}</p>
                        </div>
                        {authMessage && (
                            <div className={`auth-message-text ${authMessageType === "success" ? "auth-success-text" : "auth-error-text"}`}>
                                {authMessage}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            {isRegister && (
                                <div className="auth-form-group">
                                    <label className="auth-label" htmlFor="fullName">
                                        Full Name
                                    </label>
                                    <input
                                        className="auth-input"
                                        id="fullName"
                                        name="fullName"
                                        type="text"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                        placeholder="Admin"
                                        required
                                    />
                                </div>
                            )}

                            <div className="auth-form-group">
                                <label className="auth-label" htmlFor="email">
                                    Email Address
                                </label>
                                <input
                                    className="auth-input"
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="admin@gmail.com"
                                    required
                                />
                            </div>

                            <div className={`auth-grid-two ${isRegister ? "two-cols" : ""}`}>
                                <div className="auth-form-group">
                                    <label className="auth-label" htmlFor="password">
                                        Password
                                    </label>
                                    <input
                                        className="auth-input"
                                        id="password"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>

                                {isRegister && (
                                    <div className="auth-form-group">
                                        <label className="auth-label" htmlFor="confirmPassword">
                                            Confirm Password
                                        </label>
                                        <input
                                            className="auth-input"
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type={showPassword ? "text" : "password"}
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            placeholder="••••••••"
                                            required
                                        />
                                    </div>
                                )}
                            </div>
                            <button type="submit" className={`auth-submit-button ${isRegister ? "register" : "login"}`}>
                                {isRegister ? "Create Account" : "Login"}
                            </button>
                        </form>

                        <div className="auth-footer-text">
                            {isRegister ? (
                                <>
                                    Already have an account?{' '}
                                    <button type="button" onClick={() => setMode("login")} className="auth-button-link">
                                        Log in
                                    </button>
                                </>
                            ) : (
                                <>
                                    {isRegister ? null : (
                                        <div className="auth-form-group auth-small-bottom">
                                            <button
                                                type="button"
                                                className="auth-button-link auth-forgot-password"
                                                onClick={() => {
                                                    // Add password reset flow if required
                                                }}
                                            >
                                                Forgot password
                                            </button>
                                        </div>
                                    )}
                                    Don't have an account?{' '}
                                    <button type="button" onClick={() => setMode("register")} className="auth-button-link">
                                        Create one
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Authentication;
